import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  getInvoice,
  sendInvoice,
  cancelInvoice,
  payInvoice,
  disputeInvoice,
  confirmFundInvoice,
  claimInvoice,
  confirmClaimInvoice,
  downloadInvoicePdf,
} from "@/services/invoices.service";
import { approveMilestone } from "@/services/contracts.service";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import ExpandableText from "@/components/shared/ExpandableText";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { fmtMoney, fmtDate, fmtDateTime, statusColor } from "@/lib/format";
import { invoiceCaps } from "@/lib/permissions";
import { useWalletGuard } from "@/lib/useWalletGuard";
import { usePolling } from "@/lib/usePolling";
import { fundMilestoneOnchain, settleMilestoneOnchain } from "@/lib/onchain";
import { getKubPrice } from "@/services/pricing.service";
import { NATIVE_TOKEN, PAYMENT_OPTIONS } from "@/lib/tokens";
import { Send, CheckCircle, XCircle, AlertTriangle, ArrowLeft, ExternalLink, Wallet as WalletIcon, Download, Loader2 } from "lucide-react";

const INVOICE_ACTIONS = {
  send: sendInvoice,
  cancel: cancelInvoice,
  dispute: disputeInvoice,
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
async function pollConfirm(fn, id) {
  for (let i = 0; i < 30; i++) {
    const r = await fn(id);
    if (r?.status === "confirmed") return true;
    await sleep(2500);
  }
  return false;
}

const prettyStatus = (s) => (s === "UnderReview" ? "Under review" : s);

const InvoiceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [voidOpen, setVoidOpen] = useState(false);
  const [voidReason, setVoidReason] = useState("");
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState(null);
  const [phase, setPhase] = useState(null);
  const [payMode, setPayMode] = useState("usdc");
  const [kubPrice, setKubPrice] = useState(0);
  const ensureWallet = useWalletGuard();

  const phaseLabel = { prep: "Preparing…", wallet: "Confirm in wallet…", confirm: "Confirming…" }[phase] || "Working…";
  const Busy = ({ label }) => (
    <>
      <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> {label || phaseLabel}
    </>
  );

  const load = async ({ silent = false } = {}) => {
    try { setInv(await getInvoice(id)); }
    catch (e) { if (!silent) { toast.error(e.response?.data?.detail || "Not found"); navigate("/invoices"); } }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => { getKubPrice().then((p) => setKubPrice(p?.usd || 0)).catch(() => {}); }, []);
  usePolling(() => load({ silent: true }));

  const act = async (path, body) => {
    setBusy(true);
    try {
      const res = await INVOICE_ACTIONS[path](id, body || {});
      if (path === "send" && res?.ok === false) {
        toast.error(`Email to ${res.recipient} failed — invoice not sent. Try again.`);
      } else {
        toast.success(path === "send" ? "Invoice sent" : "Done");
      }
      await load();
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
      return false;
    } finally { setBusy(false); }
  };

  const submitVoid = async () => {
    if (voidReason.trim().length < 3) { toast.error("Please enter a reason"); return; }
    const ok = await act("cancel", { reason: voidReason.trim() });
    if (ok) { setVoidOpen(false); setVoidReason(""); }
  };

  const approveLinkedMilestone = async () => {
    setBusy(true);
    try {
      await approveMilestone(inv.milestone_id);
      toast.success("Invoice approved");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const fundInvoiceOnchain = async () => {
    setBusy(true);
    setPhase("prep");
    try {
      await ensureWallet();
      toast.message("Preparing your payment…");
      const proof = await payInvoice(id, { payment_token: payMode === "kub" ? NATIVE_TOKEN : undefined });
      setPhase("wallet");
      await fundMilestoneOnchain(proof, ({ step: n, total, kind }) => {
        setStep(kind);
        toast.message(
          kind === "approve"
            ? `Step ${n} of ${total}: approve USDC spending in your wallet…`
            : total > 1
              ? `Step ${n} of ${total}: confirm the deposit in your wallet…`
              : "Confirm the deposit in your wallet…",
        );
      });
      setPhase("confirm");
      toast.message("Waiting for on-chain confirmation…");
      const ok = await pollConfirm(confirmFundInvoice, id);
      if (ok) {
        toast.success("Invoice paid into escrow");
        await load();
      } else {
        toast.error("Not confirmed yet, refresh shortly");
      }
    } catch (e) {
      toast.error(e.shortMessage || e.response?.data?.detail || e.message || "Payment failed");
    } finally {
      setBusy(false);
      setStep(null);
      setPhase(null);
    }
  };

  const claimInvoiceOnchain = async () => {
    setBusy(true);
    setPhase("prep");
    try {
      await ensureWallet();
      toast.message("Preparing your claim…");
      const proof = await claimInvoice(id);
      setPhase("wallet");
      toast.message("Confirm the transaction in your wallet…");
      await settleMilestoneOnchain(proof);
      setPhase("confirm");
      toast.message("Waiting for on-chain confirmation…");
      const ok = await pollConfirm(confirmClaimInvoice, id);
      if (ok) {
        toast.success("Payment claimed");
        await load();
      } else {
        toast.error("Not confirmed yet, refresh shortly");
      }
    } catch (e) {
      toast.error(e.shortMessage || e.response?.data?.detail || e.message || "Claim failed");
    } finally {
      setBusy(false);
      setPhase(null);
    }
  };

  if (!inv) return <div className="text-muted-foreground">Loading…</div>;

  const caps = invoiceCaps(inv);

  const escrowFunded = ["Funded", "SettlePending"].includes(inv.onchain_status);
  const payeeCanClaim =
    inv.is_payee &&
    escrowFunded &&
    inv.status !== "Paid" &&
    inv.status !== "Cancelled" &&
    inv.dispute_resolution !== "RefundToPayer";
  const payerCanReclaim =
    inv.is_payer &&
    escrowFunded &&
    (inv.status === "Cancelled" || inv.dispute_resolution === "RefundToPayer");

  return (
    <div className="space-y-6 max-w-3xl" data-testid="invoice-detail-page">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate("/invoices")} data-testid="invoice-back-btn"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
      </div>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold font-mono">{inv.invoice_id}</div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1">{inv.title}</h1>
          <div className="mt-2 text-sm text-muted-foreground">
            <span className="font-medium">{inv.payee_name}</span> → {inv.payer_name || inv.payer_email}
            {inv.contract_id && (
              <> · linked to <Link to={`/contracts/${inv.contract_id}`} className="text-primary hover:underline">contract</Link></>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${statusColor(inv.status)}`} data-testid="invoice-status-pill">{prettyStatus(inv.status)}</span>
          {!inv.milestone_id && inv.onchain_status === "Funded" && inv.status !== "Paid" && (
            <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200" data-testid="invoice-onchain-pill">
              Amount deposited
            </span>
          )}
          <div className="font-display font-bold text-3xl">{fmtMoney(inv.amount, inv.currency)}</div>
          {inv.due_date && <div className="text-xs text-muted-foreground">Due {fmtDate(inv.due_date)}</div>}
        </div>
      </div>

      {inv.description && (
        <div className="bg-card border border-border rounded-xl p-5">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">Description</div>
          <ExpandableText text={inv.description} title="Description" className="mt-2 text-sm leading-relaxed" clamp="line-clamp-4" testid="invoice-description" />
        </div>
      )}

      {inv.attachments?.length > 0 && (
        <div className="bg-card border border-border rounded-xl p-5">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">Attachments</div>
          <ul className="mt-2 space-y-1 text-sm">
            {inv.attachments.map((a, i) => (
              <li key={i}><a href={a} target="_blank" rel="noreferrer" className="text-primary hover:underline break-all">{a}</a></li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          onClick={async () => {
            try {
              const blob = await downloadInvoicePdf(id);
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `invoice-${id}.pdf`;
              a.click();
              URL.revokeObjectURL(url);
            } catch {
              toast.error("Download failed");
            }
          }}
          data-testid="invoice-download-pdf-btn"
        >
          <Download className="w-4 h-4 mr-1.5" /> Download PDF
        </Button>
        {caps.can_send && (
          <Button onClick={() => act("send")} disabled={busy} data-testid="invoice-send-btn">
            <Send className="w-4 h-4 mr-1.5" /> Send invoice
          </Button>
        )}
        {caps.can_cancel && (
          <Button variant="outline" onClick={() => setVoidOpen(true)} disabled={busy} data-testid="invoice-void-btn">
            <XCircle className="w-4 h-4 mr-1.5" /> Void invoice
          </Button>
        )}
        {inv.milestone_id ? (
          <>
            {inv.contract_id && (
              <Button variant="outline" onClick={() => navigate(`/contracts/${inv.contract_id}`)} data-testid="invoice-view-milestone-btn">
                <ExternalLink className="w-4 h-4 mr-1.5" /> View milestone
              </Button>
            )}
            {inv.is_payer && inv.milestone_status === "Submitted" && (
              <Button onClick={approveLinkedMilestone} disabled={busy} data-testid="invoice-approve-btn">
                <CheckCircle className="w-4 h-4 mr-1.5" /> {busy ? "Approving…" : "Approve"}
              </Button>
            )}
          </>
        ) : (
          <>
            {caps.can_pay && !escrowFunded && inv.onchain_status !== "Settled" && (
              <div className="flex flex-wrap items-center gap-2">
                <div className="inline-flex rounded-lg border border-border p-0.5">
                  {PAYMENT_OPTIONS.map((o) => (
                    <button
                      key={o.mode}
                      type="button"
                      onClick={() => setPayMode(o.mode)}
                      className={`px-3 h-9 rounded-md text-sm font-medium transition-colors ${payMode === o.mode ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                      data-testid={`invoice-token-${o.mode}-btn`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
                <Button onClick={fundInvoiceOnchain} disabled={busy} data-testid="invoice-fund-btn">
                  {busy ? (
                    <Busy label={phase === "wallet" && step === "approve" ? "Approve in wallet…" : undefined} />
                  ) : (
                    <>
                      <WalletIcon className="w-4 h-4 mr-1.5" /> Pay invoice
                    </>
                  )}
                </Button>
                {payMode === "kub" && kubPrice > 0 && (
                  <span className="text-xs text-muted-foreground">≈ {(inv.amount / kubPrice).toFixed(2)} KUB</span>
                )}
              </div>
            )}
            {payeeCanClaim && (
              <Button onClick={claimInvoiceOnchain} disabled={busy} data-testid="invoice-claim-btn">
                {busy ? <Busy /> : (<><WalletIcon className="w-4 h-4 mr-1.5" /> Claim payment</>)}
              </Button>
            )}
            {payerCanReclaim && (
              <Button onClick={claimInvoiceOnchain} disabled={busy} data-testid="invoice-reclaim-btn">
                {busy ? <Busy /> : (<><WalletIcon className="w-4 h-4 mr-1.5" /> Reclaim funds</>)}
              </Button>
            )}
          </>
        )}
        {caps.can_dispute && (
          <Button variant="outline" onClick={() => setDisputeOpen(true)} disabled={busy} data-testid="invoice-dispute-btn">
            <AlertTriangle className="w-4 h-4 mr-1.5" /> Open dispute
          </Button>
        )}
        {inv.status === "Paid" && (
          <div className="inline-flex items-center gap-1.5 text-sm text-emerald-700 dark:text-emerald-400 font-medium">
            <CheckCircle className="w-4 h-4" /> Paid {inv.paid_at && <>on {fmtDateTime(inv.paid_at)}</>}
          </div>
        )}
      </div>

      <div className="bg-card border border-border rounded-xl p-5">
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-3">Activity</div>
        <ol className="space-y-3" data-testid="invoice-activity-log">
          {(inv.timeline || []).map((ev, i) => (
            <li key={i} className="flex gap-3">
              <div className="w-2 h-2 rounded-full bg-primary mt-1.5 shrink-0" />
              <div className="flex-1">
                <div className="text-sm"><span className="font-semibold">{ev.event}</span> <span className="text-muted-foreground">by {ev.by_name}</span></div>
                {ev.note && <ExpandableText text={ev.note} title={ev.event} className="text-xs text-muted-foreground mt-0.5" />}
                <div className="text-[11px] text-muted-foreground mt-0.5">{fmtDateTime(ev.at)}</div>
              </div>
            </li>
          ))}
        </ol>
      </div>

      <Dialog open={disputeOpen} onOpenChange={setDisputeOpen}>
        <DialogContent data-testid="invoice-dispute-modal">
          <DialogHeader><DialogTitle>Dispute invoice</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Reason *</Label>
              <select value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1.5 h-11 w-full rounded-md border border-input bg-background px-3 text-sm accent-primary transition-colors focus:outline-none focus:ring-1 focus:ring-ring focus:border-ring" data-testid="invoice-dispute-reason-select">
                <option value="">Select…</option>
                <option value="Work not delivered">Work not delivered</option>
                <option value="Quality issue">Quality issue</option>
                <option value="Amount wrong">Amount wrong</option>
                <option value="Already paid">Already paid</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <Label>Details</Label>
              <Textarea value={details} onChange={(e) => setDetails(e.target.value)} rows={4} className="mt-1.5" data-testid="invoice-dispute-details-input" />
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={async () => {
                if (!reason) { toast.error("Reason required"); return; }
                const ok = await act("dispute", { reason, details });
                if (ok) { setDisputeOpen(false); setReason(""); setDetails(""); }
              }}
              disabled={busy}
              data-testid="invoice-dispute-confirm-btn"
            >
              Open dispute
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={voidOpen} onOpenChange={setVoidOpen}>
        <DialogContent data-testid="invoice-void-modal">
          <DialogHeader><DialogTitle>Void invoice</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Voiding this invoice makes it permanently unpayable. The recipient will be notified by email and in-app. This can't be undone.
            </p>
            <div>
              <Label>Reason *</Label>
              <Textarea
                value={voidReason}
                onChange={(e) => setVoidReason(e.target.value)}
                rows={3}
                placeholder="Why is this invoice being voided?"
                className="mt-1.5"
                data-testid="invoice-void-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVoidOpen(false)} disabled={busy}>Keep invoice</Button>
            <Button variant="destructive" onClick={submitVoid} disabled={busy} data-testid="invoice-void-confirm-btn">
              Void invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InvoiceDetail;
